import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Typography,
    useTheme,
    Stack,
    TextField,
    Grid,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import InputField from '../../components/formItem/inputField';
import Dropdown from '../../components/dropdown';
import CommonCard from '../../components/card/CommonCard';
import RadioBt, { RadioGroup } from '../../components/radioBt';
import { championAPI, contentAPI, membershipAPI } from '../../actions/imsServer';
import { CHAMPIONSHIP_TYPES, TOURNAMENT_PARTICIPANT_OPTIONS, COUNTRY_OPTIONS, API_PAGE_SIZE } from '../../constants';
import { formatDateForMySQL, isAdmin, toDisplayString, getLocalizedName } from '../../utils/base';

const periods = [7, 14, 30, 60];
// InputField 래퍼 컴포넌트
const InputFieldComponent = ({ title, children, sx }) => {
    return (
        <Box sx={{
            //    height: '72px',
            alignItems: 'center',
            ...sx
        }}>
            <Typography sx={{
                mb: 1,
                fontWeight: 500,
                fontSize: '14px',
                letterSpacing: '-0.42px',
                textAlign: 'left'
            }}>
                {title}
            </Typography>
            {children}
        </Box>
    );
};

const ChampionshipAdminAdd = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const [meInfo, setMeInfo] = useState(null);

    useEffect(() => {
        const meInfo = JSON.parse(localStorage.getItem('meInfo'));
        setMeInfo(meInfo);
    }, []);

    const [form, setForm] = useState({
        name: '',
        contentIndex: '',
        type: 0, // 0: 랭킹, 1: 토너먼트 (CHAMPIONSHIP_TYPES value)
        period: 7,
        playerCount: 16, // 토너먼트 참여인원
        isPublic: true,
        password: '', // 비공개 대회 비밀번호
        rewardMessage: '',
        scope: 'ko', // 'ko' - 대회범위 (기본값: 대한민국)
    });

    const [contents, setContents] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const allContents = [];
                const limit = API_PAGE_SIZE;
                let offset = 0;
                let hasMore = true;

                while (hasMore) {
                    const contentsRes = await contentAPI.getContents({ state: 0, limit, offset });
                    const list = contentsRes?.data?.contents ?? contentsRes?.contents ?? [];
                    const arr = Array.isArray(list) ? list : [];
                    allContents.push(...arr);
                    if (arr.length < limit) {
                        hasMore = false;
                    } else {
                        offset += limit;
                    }
                }

                setContents(allContents.filter(content => content.championship === 1));
            } catch (e) {
                setContents([]);
            }
        };
        load();
    }, []);

    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const isValid = () => {
        if (!form.name || form.name.length > 24) return false;
        if (!form.contentIndex) return false;
        if (form.type === 0 && !form.period) return false; // 랭킹
        if (form.type === 1 && !form.playerCount) return false; // 토너먼트
        if (!isAdmin(meInfo?.grade) && !form.scope) return false; // 어드민이 아닐 때만 scope 체크
        // 비공개인 경우 비밀번호 필수
        if (!form.isPublic && !form.password) return false;
        return true;
    };

    const handleCancel = () => navigate('/championships');

    const handleSubmit = async () => {
        if (!isValid()) return;
        setSaving(true);
        try {
            const now = new Date();
            const startDate = formatDateForMySQL(now);
            // 랭킹인 경우만 endDate 계산 (사용자 로컬 기준: 당일 00:00 ~ 당일+periodDays 23:59:59)
            let endDate = undefined;
            if (form.type === 0) {
                const periodDays = form.period;
                const end = new Date(now.getTime() + periodDays * 24 * 60 * 60 * 1000);
                end.setHours(23, 59, 59, 999); // 해당일 로컬 자정 직전 → formatDateForMySQL가 로컬 YYYY-MM-DD 23:59:59 반환
                endDate = formatDateForMySQL(end);
            }

            // 어드민인 경우 기관 ID 가져오기
            let organizationId = 0;
            if (isAdmin(meInfo?.grade) && meInfo?.id) {
                try {
                    // grade 50, 60 모두 membership에서 가져오기
                    const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                    const memberships = membershipsResponse?.data?.memberships || [];
                    if (memberships.length > 0) {
                        organizationId = memberships[0].organizationId;
                    }
                } catch (err) {
                    console.error('Failed to fetch user organizations:', err);
                    // 에러 발생 시 기본값 0 사용
                }
            }

            await championAPI.registerChampion({
                name: form.name,
                organizationId: organizationId,
                userId: meInfo?.id,
                contentIndex: form.contentIndex,
                type: form.type,
                startDate,
                endDate,
                playerCount: form.type === 1 ? form.playerCount : null,
                password: !form.isPublic ? form.password : '',
                rewardMessage: form.rewardMessage,
            });

            navigate('/championships');
        } catch (e) {
            console.error('챔피언십 생성 실패:', e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <ContentArea padding='20px 10px 10px 10px'>
            {/* 폼 영역 */}
            <Box sx={{ px: 3, pb: 4, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={4} sx={{ flex: 1 }}>
                    {/* 왼쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={t('championship_name') + ' *'}>
                                <InputField
                                    placeholder={t('max_24_characters')}
                                    value={form.name}
                                    onChange={(e) => setField('name', e.target.value)}
                                    error={!!form.name && form.name.length > 24}
                                    helperText={(form.name && form.name.length > 24) ? t('please_enter_within_24_characters') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('championship_event') + ' *'}>
                                <Dropdown
                                    options={(contents || []).map(c => ({
                                        value: c.index,
                                        label: getLocalizedName(c, i18n.language)
                                    }))}
                                    value={form.contentIndex}
                                    onChange={(opt) => setField('contentIndex', opt.value)}
                                    placeholder={t('please_select')}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('championship_format') + ' *'}>
                                <Stack direction="row" spacing={2}>
                                    {CHAMPIONSHIP_TYPES.slice().reverse().map(type => (
                                        <Button
                                            key={type.value}
                                            variant={form.type === type.value ? 'normal' : 'gray'}
                                            onClick={() => setField('type', type.value)}
                                            sx={{ minWidth: '120px' }}
                                        >
                                            {t(type.label)}
                                        </Button>
                                    ))}
                                </Stack>
                            </InputFieldComponent>

                            {form.type === 0 ? (
                                <InputFieldComponent title={t('championship_period') + ' *'}>
                                    <Stack direction="row" spacing={2}>
                                        {periods.map(p => (
                                            <Button
                                                key={p}
                                                variant={form.period === p ? 'normal' : 'gray'}
                                                onClick={() => setField('period', p)}
                                                size="small"
                                                sx={{ minWidth: '80px' }}
                                            >
                                                {t(`${p}_days`)}
                                            </Button>
                                        ))}
                                    </Stack>
                                </InputFieldComponent>
                            ) : (
                                <InputFieldComponent title={t('participants') + ' *'}>
                                    <Stack direction="row" spacing={2}>
                                        {TOURNAMENT_PARTICIPANT_OPTIONS.map(option => (
                                            <Button
                                                key={option.value}
                                                variant={form.playerCount === option.value ? 'normal' : 'gray'}
                                                onClick={() => setField('playerCount', option.value)}
                                                size="small"
                                                sx={{ minWidth: '80px' }}
                                            >
                                                {toDisplayString(t(option.label), option.label)}
                                            </Button>
                                        ))}
                                    </Stack>
                                </InputFieldComponent>
                            )}

                            <InputFieldComponent title={t('public_private') + ' *'} sx={{ display: 'flex', flexDirection: 'row' }}>
                                <RadioGroup
                                    value={form.isPublic ? 'public' : 'private'}
                                    onChange={(e) => setField('isPublic', e.target.value === 'public')}
                                >
                                    <RadioBt value="public" label={t('public')} />
                                    <RadioBt value="private" label={t('private')} />
                                </RadioGroup>
                            </InputFieldComponent>

                            {!form.isPublic && (
                                <InputField
                                    type="password"
                                    showVisibilityIcon={true}
                                    placeholder={t('4_digits')}
                                    value={form.password}
                                    onChange={(e) => setField('password', e.target.value)}
                                    error={!form.isPublic && !form.password}
                                    helperText={!form.isPublic && !form.password ? t('please_enter_password') : ' '}
                                />
                            )}
                        </CommonCard>
                    </Grid>

                    {/* 오른쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={t('reward_condition')} sx={{ height: 'auto' }}>
                                <TextField
                                    placeholder={t('please_enter_reward_condition')}
                                    value={form.rewardMessage}
                                    onChange={(e) => setField('rewardMessage', e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={8}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            backgroundColor: theme.palette.figma.white[5],
                                        }
                                    }}
                                />
                            </InputFieldComponent>

                            {!isAdmin(meInfo?.grade) && (
                                <InputFieldComponent title={t('championship_scope') + ' *'}>
                                    <Dropdown
                                        options={COUNTRY_OPTIONS}
                                        value={form.scope}
                                        onChange={(opt) => setField('scope', opt.value)}
                                        placeholder={t('please_select')}
                                        disabled={true}
                                    />
                                </InputFieldComponent>
                            )}
                        </CommonCard>
                    </Grid>
                </Grid>

                {/* 하단 버튼 */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    gap: 2,
                    px: 2.5,
                    pb: 3,
                    alignSelf: 'stretch',
                    marginTop: 'auto'
                }}>
                    <Button variant="gray" onClick={handleCancel} sx={{ minWidth: '120px' }}>{t('cancel')}</Button>
                    <Button onClick={handleSubmit} disabled={saving || !isValid()} sx={{ minWidth: '120px' }}>{t('confirm')}</Button>
                </Box>
            </Box>
        </ContentArea>
    );
};

export { ChampionshipAdminAdd };


